import i18n from '@/utils/i18n';
import { captureException } from '@/utils/sentry';
import { ChatProspectStage } from '@/types';
import { useCallback, useState } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useSalesPortalUpdateProspectDetailsMutation } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/blocs/documents/sales-portal-update-prospect-details.generated';

interface UpdateProspectStageMutatorParams {
  loadedStage: ChatProspectStage | null;
  restaurantUUID: string;
  supplierUUID: string;
}

interface UpdateProspectStageMutatorResult {
  selectedStage: ChatProspectStage | null;
  selectStage: (stage: ChatProspectStage) => Promise<void>;
}

/**
 * Persists a stage pick via `updateSalesPortalProspectDetails`.
 *
 * Holds the stage locally and shows it optimistically.
 *
 * The mutation response carries the same `ProspectDetails_Fields` fragment,
 * keyed by uuid, as the stage query, so a success normalizes straight into
 * the shared cache entry with no refetch or loading state; once `loadedStage`
 * catches up to the pick the local copy is cleared so the cache value shows
 * from then on. On failure it rolls the pick back and toasts an error.
 *
 * @param params.loadedStage - The stage the stage query currently holds.
 * @param params.restaurantUUID - The UUID of the restaurant.
 * @param params.supplierUUID - The UUID of the supplier.
 * @returns `selectedStage` — the locally-stored stage, null until the user picks one;
 *   `selectStage(stage)` — fires the mutation.
 */
export const useUpdateProspectStageMutator = ({
  loadedStage,
  restaurantUUID,
  supplierUUID,
}: UpdateProspectStageMutatorParams): UpdateProspectStageMutatorResult => {
  const { showErrorToast } = useExperienceToasts();
  const [updateProspectDetails] = useSalesPortalUpdateProspectDetailsMutation();
  const [selectedStage, setSelectedStage] = useState<ChatProspectStage | null>(
    null,
  );

  if (selectedStage !== null && selectedStage === loadedStage) {
    setSelectedStage(null);
  }

  const selectStage = useCallback(
    async (stage: ChatProspectStage) => {
      const previousStage = selectedStage;
      setSelectedStage(stage);
      try {
        await updateProspectDetails({
          variables: {
            prospectDetails: { stage },
            restaurantUUID,
            supplierUUID,
          },
        });
      } catch (error) {
        captureException(error);
        setSelectedStage((current) =>
          current === stage ? previousStage : current,
        );
        showErrorToast(
          i18n.t('sales_portal.prospect_detail.stage.update_error'),
        );
      }
    },
    [
      restaurantUUID,
      selectedStage,
      showErrorToast,
      supplierUUID,
      updateProspectDetails,
    ],
  );

  return { selectedStage, selectStage };
};
